/**
 * --------------------------------------------------
 * Search Module
 * 
 * Takes care of frontend search functionality,
 * including opening and closing the search modal.
 * 
 * Also has the endpoints for search queries.
 *
 * @module Search
 * --------------------------------------------------
 */

import gsap from 'gsap';

export default {
    _triggerSelector: '.rt-js-search-icon',
    _barSelector: '.rt-js-search-bar',
    _wrapperSelector: '.rt-js-search-wrapper',
    _closeSelector: '.rt-js-search-close',
    _inputSelector: '.rt-js-search-input',
    _clearInputSelector: '.rt-js-clear-input',
    _resultsSelector: '.rt-js-results',
    _resultsListSelector: '.rt-js-results-list',
    _internalIconSelector: '.rt-search-bar__icon',
    _formSelector: 'form[role="search"]',
    _submitSelector: 'button[type="submit"]',
    _isOpen: false,
    _resultsVisible: false,
    _minChars: 2,
    _endpoint: null,
    _limit: 8,
    _debounceTimer: null,

    // Initialize the search module
    setup: function() {
        this.$trigger = $(this._triggerSelector);
        this.$bar = $(this._barSelector);
        this.$wrapper = $(this._wrapperSelector);
        this.$close = $(this._closeSelector);
        this.$input = $(this._inputSelector);
        this.$results = $(this._resultsSelector);
        this.$resultsList = $(this._resultsListSelector);
        this.$internalIcon = $(this._internalIconSelector);
        this.$clearInputBtn = $(this._clearInputSelector);
        this.$form = $(this._formSelector);
        this.$submitBtn = this.$form.find(this._submitSelector);
        this._endpoint = (window.rtSearchSettings && window.rtSearchSettings.restUrl) ? window.rtSearchSettings.restUrl : null;
        this._minChars = (window.rtSearchSettings && window.rtSearchSettings.minChars) ? window.rtSearchSettings.minChars : this._minChars;
        this._limit = (window.rtSearchSettings && window.rtSearchSettings.limit) ? window.rtSearchSettings.limit : this._limit;

        if (!this.$bar.length) {
            return;
        }

        //this._initializeSubmitButton(); // Commented out to allow form submission without JS, can be re-enabled if needed
        this._bindEvents();
    },

    // Bind event listeners
    _bindEvents: function() {
        const _self = this;

        this.$trigger.on('click', function(e) {
            e.preventDefault();
            _self.open();
        });

        // Clear sibling input when the close button is clicked (block search toggle)
        $(document).on('click', _self._clearInputSelector, function() {
            const $btn = $(this);
            const $input = $btn.siblings('.rt-js-search-blk-input'); 
            if ($input.val().trim() !== '' && $input.hasClass('hidden')) {
                    $input.val('').trigger('input');
            }
        });

        this.$input.on('input', function() {
            const value = $(this).val().trim();
            _self._updateSubmitButtonState();

            if (value.length >= _self._minChars) {
                _self._showLoadingState();
                _self._debouncedFetch(value);
            } else {
                clearTimeout(_self._debounceTimer);
                _self._clearResults();
                _self._hideResults(false);
            }
        });

        // Close search on close button, Escape key, or click outside
        $(document).on('keydown click', function(e) {
            if (!_self._isOpen) return;
            
            const $target = $(e.target);
            
            // Close on close button click
            if (e.type === 'click' && $target.closest(_self._closeSelector).length > 0) {
                e.preventDefault();
                _self.close();
                return;
            }
            
            // Close on Escape key
            if (e.type === 'keydown' && e.key === 'Escape') {
                _self.close();
                return;
            }
            
            // Close when clicking outside
            if (e.type === 'click') {
                const isInsideBar = $target.closest(_self._barSelector).length > 0;
                const isInsideWrapper = $target.closest(_self._wrapperSelector).length > 0;
                const isTrigger = $target.closest(_self._triggerSelector).length > 0;
                
                if (!isInsideBar && !isInsideWrapper && !isTrigger) {
                    _self.close();
                }
            }
        });
    },

    // Open the search bar
    open: function() {
        if (this._isOpen) return;

        this._isOpen = true;

        gsap.killTweensOf([this.$bar, this.$wrapper, this.$trigger]);

        const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

        tl.to( 
            this.$trigger,
            { autoAlpha: 0, duration: 0.08 },   
        )

        tl.fromTo(
            this.$bar,
            { autoAlpha: 0, y: -12 },
            { autoAlpha: 1, y: 0, duration: 0.1 },
            '>0.3'
        );

        if (this.$wrapper.length) {
            tl.fromTo(
                this.$wrapper,
                { scaleX: 0.5, opacity: 0, transformOrigin: 'right center' },
                { scaleX: 1, duration: 0.2, opacity: 1}
            );
        }

        tl.add(() => {
            if (this.$input.length) {
                this.$input.trigger('focus');
            }
        });
    },

    // Close the search bar
    close: function() {
        if (!this._isOpen) return;

        this._isOpen = false;
        gsap.killTweensOf([this.$bar, this.$wrapper]);

        const tl = gsap.timeline({ defaults: { ease: 'power2.in' } });

        if (this.$wrapper.length) {
            tl.to(this.$wrapper, { duration: 0.2 });
        }

        tl.to(this.$bar, {
            autoAlpha: 0,
            duration: 0.2,
            y: -10,
            onComplete: () => {
                gsap.set(this.$bar, { clearProps: 'all' });

                // Hide the container if open, and clear input
                this._clearResults();
                this._hideResults(true);
            }
        }, '<');

        tl.to( 
            this.$trigger,
            { autoAlpha: 1, duration: 0.2 },
            '<'
        )
    },

    // Initialize submit button state and listeners
    _initializeSubmitButton: function() {
        const _self = this;
        // Set initial state
        this._updateSubmitButtonState();
        
        // Prevent form submission if search input is empty
        this.$form.on('submit', function(e) {
            if (_self.$input.val().trim() === '') {
                e.preventDefault();
            }
        });
    },

    // Update submit button disabled state based on input value
    _updateSubmitButtonState: function() {
        const hasValue = this.$input.val().trim() !== '';
        this.$submitBtn.prop('disabled', !hasValue);
    },

    // Show search results with animation
    _showResults: function() {
        if (this._resultsVisible) return;
        this._resultsVisible = true;

        if (!this.$results.length) return;

        gsap.killTweensOf(this.$results);
        gsap.fromTo(this.$results, 
            { autoAlpha: 0, scaleY: 0 },
            { autoAlpha: 1, scaleY: 1, duration: 0.15, ease: 'power2.out' }
        );
    },

    // Hide search results with animation
    _hideResults: function(clearInput) {
        if (!this._resultsVisible) {
            if (clearInput) {
                this.$input.val('');
            }
            return;
        }
        this._resultsVisible = false;

        if (!this.$results.length) return;

        gsap.killTweensOf(this.$results);
        gsap.to(this.$results, {
            autoAlpha: 0,
            scaleY: 0,
            duration: 0.15,
            ease: 'power2.in',
            onComplete: () => {
                if (clearInput) {
                    // Clear the input field when closing the search
                    this.$input.val('');
                }
            }
        });
    },

    _debouncedFetch: function(query) {
        if (!this._endpoint || !this.$resultsList.length) return;

        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
            this._fetchResults(query);
        }, 200);
    },

    _showLoadingState: function() {
        if (!this.$resultsList.length) return;

        // Show container immediately
        if (!this._resultsVisible) {
            this._showResults();
        }

        // Show loading spinner
        const loader = '<li class="rt-search-loading"><div class="rt-search-loading__spinner"></div></li>';
        this.$resultsList.html(loader);
    },

    _fetchResults: function(query) {
        if (!this._endpoint) return;

        const url = new URL(this._endpoint);
        url.searchParams.set('q', query);
        url.searchParams.set('limit', this._limit);

        fetch(url.toString(), { credentials: 'same-origin' })
            .then(response => response.ok ? response.json() : [])
            .then(data => {
                const items = Array.isArray(data) ? data : [];
                this._renderResults(items);
            })
            .catch(() => {
                this._clearResults();
                this._hideResults(false);
            });
    },

    _renderResults: function(items) {
        if (!this.$resultsList.length) return;

        if (!items.length) {
            this._clearResults();
            this._hideResults(false);
            return;
        }

        const html = items.map(item => {
            const term = this._escapeHtml(item.term || '');
            const url = this._escapeHtml(item.url || '#');
            return `<li><a href="${url}">${term}</a></li>`;
        }).join('');

        this.$resultsList.html(html);
        this._showResults();
    },

    _clearResults: function() {
        if (this.$resultsList && this.$resultsList.length) {
            this.$resultsList.empty();
        }
    },

    _escapeHtml: function(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
